/**
 * Register Modal Component
 * 
 * Modal form for user registration with validation and error handling.
 * Integrates with the authentication system and provides user feedback.
 */

import React, { useState, FormEvent, useEffect } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import Toast from '../common/Toast';
import { apiService } from '../../services/api';
import './RegisterModal.css';

// Type definitions
interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (response: any) => void;
}

interface FormData {
  username: string;
  password: string;
  confirmPassword: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface FormErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  general?: string;
}

/**
 * Register Modal component
 */
const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    firstName: '',
    lastName: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showToast, setShowToast] = useState<boolean>(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        username: '',
        password: '',
        confirmPassword: '',
        email: '',
        firstName: '',
        lastName: '',
      });
      setErrors({});
      setSuccessMessage('');
      setShowToast(false);
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  /**
   * Handle input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific errors when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Clear general error when user starts typing
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: undefined,
      }));
    }

    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Field Required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'At least 3 characters';
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = 'Field Required';
    } else if (formData.password.length < 4) {
      newErrors.password = 'At least 4 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email required';
    }

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Field Required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Field Required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiService.post('/auth/register', {
        username: formData.username,
        password: formData.password,
        email: formData.email,
        firstname: formData.firstName,
        lastname: formData.lastName,
      });

      // Show success message
      const message = response.data?.detail || 'Success!. Please check your email for verification link.';
      setSuccessMessage(message);
      setShowToast(true);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Ensure toast is visible before closing modal
      setTimeout(() => {
        setShowToast(false);
      }, 5000);

      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error: any) {
      let message = 'Registration failed. Please try again.';

      // Handle different error formats including arrays of validation errors
      if (error.message) {
        message = error.message;
      } else if (error.detail) {
        message = error.detail;
      } else if (Array.isArray(error)) {
        // If error is an array of validation errors, join messages
        message = error.map((err) => {
          if (typeof err === 'string') return err;
          if (err.msg) return err.msg;
          return JSON.stringify(err);
        }).join('; ');
      } else if (typeof error === 'object') {
        // If error is an object with keys, try to extract messages
        message = Object.values(error).map((val) => {
          if (typeof val === 'string') return val;
          if (Array.isArray(val)) {
            return val.map((e) => (e.msg ? e.msg : JSON.stringify(e))).join('; ');
          }
          return JSON.stringify(val);
        }).join('; ');
      } else if (typeof error === 'string') {
        message = error;
      }

      setErrors(prev => ({
        ...prev,
        general: message,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle backdrop click to close modal
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div className="register-modal-backdrop" onClick={handleBackdropClick}>
          <div className="register-modal" role="dialog" aria-labelledby="register-modal-title" aria-modal="true">
            <div className="register-modal__header">
              <h2 id="register-modal-title" className="register-modal__title">Create Account</h2>
              <button
                type="button"
                className="register-modal__close"
                onClick={onClose}
                aria-label="Close registration modal"
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form className="register-modal__form" onSubmit={handleSubmit} noValidate>
              {/* Success Message */}
              {successMessage && (
                <div className="register-modal__success" role="alert">
                  <svg className="register-modal__success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                  {successMessage}
                </div>
              )}

              {/* Error Display */}
              {errors.general && (
                <div className="register-modal__error" role="alert">
                  <svg className="register-modal__error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {typeof errors.general === 'string' ? errors.general : JSON.stringify(errors.general)}
                </div>
              )}

              {/* Form Fields */}
              <div className="register-modal__fields">
                {/* Username Field */}
                <div className="register-modal__field">
                  <Input
                    type="text"
                    name="username"
                    label="Username"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleInputChange}
                    error={errors.username}
                    disabled={isLoading}
                    autoComplete="username"
                    required
                  />
                </div>

                {/* Email Field */}
                <div className="register-modal__field">
                  <Input
                    type="email"
                    name="email"
                    label="Email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={errors.email}
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>

                {/* Name Fields Row */}
                  <div className="register-modal__field">
                    <Input
                      type="text"
                      name="firstName"
                      label="First Name"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      error={errors.firstName}
                      disabled={isLoading}
                      autoComplete="given-name"
                      required
                    />
                  </div>

                  <div className="register-modal__field">
                    <Input
                      type="text"
                      name="lastName"
                      label="Last Name"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      error={errors.lastName}
                      disabled={isLoading}
                      autoComplete="family-name"
                      required
                    />
                  </div>

                {/* Password Field */}
                <div className="register-modal__field">
                  <Input
                    type="password"
                    name="password"
                    label="Password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    error={errors.password}
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>

                {/* Confirm Password Field */}
                <div className="register-modal__field">
                  <Input
                    type="password"
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    error={errors.confirmPassword}
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="register-modal__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isLoading}
                  className="register-modal__cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading || !!successMessage}
                  className="register-modal__submit-button"
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showToast && (
        <Toast
          message={successMessage}
          duration={5000}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};

export default RegisterModal;
